export const THRESHOLD_SEVERITIES = ["warning", "medium", "high", "critical"] as const;
export type ThresholdSeverity = (typeof THRESHOLD_SEVERITIES)[number];

export const THRESHOLD_UNITS = ["people", "minutes", "percent_of_capacity"] as const;
export type ThresholdUnit = (typeof THRESHOLD_UNITS)[number];

export interface ThresholdValue {
  severity: ThresholdSeverity;
  value: number | "";
  unit: ThresholdUnit;
}

export interface ThresholdConfig {
  id: string;
  name: string;
  scopeLabel: string;
  metricLabel: string;
  availableUnits: readonly ThresholdUnit[];
  availableSeverities: readonly ThresholdSeverity[];
  thresholds: ThresholdValue[];
}

export interface DeploymentAreaCapacity {
  id: string;
  name: string;
  capacity: number | "";
  maxDensityPeoplePerSquareMeter?: number | null;
  capacityNotes?: string | null;
}

export interface DeploymentAreaDefinition {
  id: string;
  name: string;
}

export interface AdminSettingsConfiguration {
  thresholdConfigs: ThresholdConfig[];
  areas: DeploymentAreaCapacity[];
}

export interface SettingsChangeSummary {
  category: "Threshold" | "Area capacity";
  area: string;
  field: string;
  label: string;
  before: string;
  after: string;
}

export const AVAILABLE_DEPLOYMENT_AREAS: readonly DeploymentAreaDefinition[] = [
  { id: "security", name: "Security" },
  { id: "departures-lounge", name: "Departures Lounge" },
  { id: "check-in", name: "Check-in Hall" },
];

export function isThresholdUnit(value: string): value is ThresholdUnit {
  switch (value) {
    case "people":
    case "minutes":
    case "percent_of_capacity":
      return true;
    default:
      return false;
  }
}

export function getThresholdUnitLabel(unit: ThresholdUnit): string {
  switch (unit) {
    case "people":
      return "People";
    case "minutes":
      return "Minutes";
    case "percent_of_capacity":
      return "% of area capacity";
  }
}

export function getThresholdSeverityLabel(severity: ThresholdSeverity): string {
  switch (severity) {
    case "warning":
      return "Warning";
    case "medium":
      return "Medium";
    case "high":
      return "High";
    case "critical":
      return "Critical";
  }
}

export const DEFAULT_ADMIN_SETTINGS: AdminSettingsConfiguration = {
  thresholdConfigs: [
    {
      id: "security-east-queue-wait-time",
      name: "Queue wait time",
      scopeLabel: "Security East queue",
      metricLabel: "Estimated wait time",
      availableUnits: ["minutes"],
      availableSeverities: THRESHOLD_SEVERITIES,
      thresholds: [
        { severity: "warning", value: 8, unit: "minutes" },
        { severity: "medium", value: 10, unit: "minutes" },
        { severity: "high", value: 12, unit: "minutes" },
        { severity: "critical", value: 15, unit: "minutes" },
      ],
    },
    {
      id: "departures-population",
      name: "Area total population",
      scopeLabel: "Departures Lounge",
      metricLabel: "Total population",
      availableUnits: ["people", "percent_of_capacity"],
      availableSeverities: THRESHOLD_SEVERITIES,
      thresholds: [
        { severity: "warning", value: 50, unit: "percent_of_capacity" },
        { severity: "medium", value: 3000, unit: "people" },
        { severity: "high", value: 85, unit: "percent_of_capacity" },
        { severity: "critical", value: 100, unit: "percent_of_capacity" },
      ],
    },
  ],
  areas: [
    { id: "security", name: "Security", capacity: 1200 },
    { id: "departures-lounge", name: "Departures Lounge", capacity: 5000 },
  ],
};

export function cloneAdminSettings(
  configuration: AdminSettingsConfiguration
): AdminSettingsConfiguration {
  return {
    thresholdConfigs: configuration.thresholdConfigs.map((config) => ({
      ...config,
      availableUnits: [...config.availableUnits],
      availableSeverities: [...config.availableSeverities],
      thresholds: config.thresholds.map((threshold) => ({ ...threshold })),
    })),
    areas: configuration.areas.map((area) => ({ ...area })),
  };
}

export function getDefaultThresholdUnit(availableUnits: readonly ThresholdUnit[]): ThresholdUnit {
  if (availableUnits.includes("minutes")) {
    return "minutes";
  }

  if (availableUnits.includes("people")) {
    return "people";
  }

  return "percent_of_capacity";
}

export function validateAdminSettings(configuration: AdminSettingsConfiguration): string | null {
  for (const config of configuration.thresholdConfigs) {
    for (const threshold of config.thresholds) {
      if (threshold.value === "") {
        return `${config.name} ${getThresholdSeverityLabel(threshold.severity)} needs a value.`;
      }

      if (threshold.value < 0) {
        return `${config.name} values cannot be negative.`;
      }

      if (threshold.unit === "percent_of_capacity" && threshold.value > 100) {
        return `${config.name} percentage values must be between 0% and 100%.`;
      }
    }
  }

  if (configuration.areas.some((area) => area.capacity !== "" && area.capacity < 0)) {
    return "Area capacity values cannot be negative.";
  }

  return null;
}

export function getThresholdUnitAdornment(unit: ThresholdUnit): string {
  switch (unit) {
    case "people":
      return "people";
    case "minutes":
      return "min";
    case "percent_of_capacity":
      return "%";
  }
}

export function describeThresholdUnits(units: readonly ThresholdUnit[]): string {
  if (units.includes("minutes")) {
    return "minutes";
  }

  if (units.includes("people") && units.includes("percent_of_capacity")) {
    return "people or percentage of area capacity";
  }

  if (units.includes("people")) {
    return "people";
  }

  return "percentage of area capacity";
}

export function formatThresholdValue(threshold: ThresholdValue): string {
  if (threshold.value === "") {
    return "Not configured";
  }

  return `${threshold.value} ${getThresholdUnitLabel(threshold.unit).toLowerCase()}`;
}

export function getSettingsChangeSummary(
  current: AdminSettingsConfiguration,
  draft: AdminSettingsConfiguration
): SettingsChangeSummary[] {
  const changes: SettingsChangeSummary[] = [];

  for (const draftConfig of draft.thresholdConfigs) {
    const currentConfig = current.thresholdConfigs.find((config) => config.id === draftConfig.id);

    for (const draftThreshold of draftConfig.thresholds) {
      const currentThreshold = currentConfig?.thresholds.find(
        (threshold) => threshold.severity === draftThreshold.severity
      );
      const before =
        currentThreshold == null ? "Not configured" : formatThresholdValue(currentThreshold);
      const after = formatThresholdValue(draftThreshold);

      if (before !== after) {
        changes.push({
          category: "Threshold",
          area: draftConfig.scopeLabel,
          field: `${draftConfig.name} · ${getThresholdSeverityLabel(draftThreshold.severity)}`,
          label: `${draftConfig.name} · ${getThresholdSeverityLabel(draftThreshold.severity)}`,
          before,
          after,
        });
      }
    }

    for (const currentThreshold of currentConfig?.thresholds ?? []) {
      const stillConfigured = draftConfig.thresholds.some(
        (threshold) => threshold.severity === currentThreshold.severity
      );

      if (!stillConfigured) {
        changes.push({
          category: "Threshold",
          area: draftConfig.scopeLabel,
          field: `${draftConfig.name} · ${getThresholdSeverityLabel(currentThreshold.severity)}`,
          label: `${draftConfig.name} · ${getThresholdSeverityLabel(currentThreshold.severity)}`,
          before: formatThresholdValue(currentThreshold),
          after: "Removed",
        });
      }
    }
  }

  for (const draftArea of draft.areas) {
    const currentArea = current.areas.find((area) => area.id === draftArea.id);
    const before = currentArea == null ? "Not configured" : formatCapacity(currentArea.capacity);
    const after = formatCapacity(draftArea.capacity);

    if (before !== after) {
      changes.push({
        category: "Area capacity",
        area: draftArea.name,
        field: "Capacity",
        label: draftArea.name,
        before,
        after,
      });
    }
  }

  for (const currentArea of current.areas) {
    if (!draft.areas.some((area) => area.id === currentArea.id)) {
      changes.push({
        category: "Area capacity",
        area: currentArea.name,
        field: "Capacity",
        label: currentArea.name,
        before: formatCapacity(currentArea.capacity),
        after: "Removed",
      });
    }
  }

  return changes;
}

function formatCapacity(capacity: number | ""): string {
  return capacity === "" ? "Not configured" : `${capacity} people`;
}
